import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Alert, Badge } from 'react-bootstrap';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import SectionContainer from '../../components/UI/SectionContainer';
import Spinner from '../../components/UI/Spinner';
import securityMonitoringService from '../../services/securityMonitoringService';
import { openAlert } from '../../utils/common';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const SecurityMonitoring = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [recentEvents, setRecentEvents] = useState([]);
    const intervalRef = useRef(null);

    useEffect(() => {
        fetchDashboardData();
        fetchRecentEvents();
        
        // 30초마다 자동 새로고침
        const interval = setInterval(() => {
            fetchDashboardData();
            fetchRecentEvents();
        }, 30000);
        
        return () => clearInterval(interval);
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const result = await securityMonitoringService.getDashboardOverview('24h');
            
            if (result.success) {
                setDashboardData(result.data);
                setError(null);
            } else {
                setError(result.message);
                openAlert({ title: '알림', description: result.message });
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || '대시보드 데이터를 불러오는 중 오류가 발생했습니다.';
            setError(errorMessage);
            openAlert({ title: '알림', description: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    const fetchRecentEvents = async () => {
        try {
            const result = await securityMonitoringService.getRecentSecurityEvents(20);
            
            if (result.success) {
                setRecentEvents(result.data);
            } else {
                setRecentEvents([]);
                // 보안 이벤트는 선택적 데이터이므로 알림 표시하지 않음
                console.warn('보안 이벤트 조회 실패:', result.message);
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || '보안 이벤트를 불러오는 중 오류가 발생했습니다.';
            setRecentEvents([]);
            console.error('보안 이벤트 조회 오류:', errorMessage);
        }
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '-';
        
        const date = new Date(timestamp);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);
        
        if (diffInSeconds < 60) {
            return `${diffInSeconds}초 전`;
        } else if (diffInSeconds < 3600) {
            return `${Math.floor(diffInSeconds / 60)}분 전`;
        } else if (diffInSeconds < 86400) {
            return `${Math.floor(diffInSeconds / 3600)}시간 전`;
        } else {
            return date.toLocaleDateString('ko-KR') + ' ' + date.toLocaleTimeString('ko-KR');
        }
    };

    const getSecurityEventsChartData = () => {
        if (!dashboardData?.integrity_monitoring?.security_events) return null;

        const { security_events } = dashboardData.integrity_monitoring;
        const bySeverity = security_events.by_severity || {};
        
        return {
            labels: ['Critical', 'High', 'Medium', 'Low'],
            datasets: [{
                data: [
                    bySeverity.critical || 0,
                    bySeverity.high || 0,
                    bySeverity.medium || 0,
                    bySeverity.low || 0
                ],
                backgroundColor: [
                    '#dc3545',
                    '#fd7e14',
                    '#ffc107',
                    '#28a745'
                ],
                borderWidth: 2,
                borderColor: '#fff'
            }]
        };
    };

    const getIntegrityChartData = () => {
        if (!dashboardData?.integrity_monitoring?.integrity_checks) return null;

        const { integrity_checks } = dashboardData.integrity_monitoring;
        return {
            labels: ['성공', '실패'],
            datasets: [{
                label: '무결성 검증 결과',
                data: [
                    integrity_checks.passed_count || 0, 
                    integrity_checks.failed_count || 0
                ],
                backgroundColor: ['#28a745', '#dc3545'],
                borderColor: ['#1e7e34', '#c82333'],
                borderWidth: 1
            }]
        };
    };

    const calculateSystemHealth = () => {
        if (!dashboardData?.summary) return 0;
        return dashboardData.summary.integrity_score || 0;
    };

    const getSeverityBadge = (severity) => {
        const variants = {
            'LOW': 'info',
            'MEDIUM': 'warning',
            'HIGH': 'danger',
            'CRITICAL': 'dark'
        };
        return <Badge bg={variants[severity] || 'secondary'} className="text-white">{severity}</Badge>;
    };

    if (loading) {
        return (
            <Container className="mt-4">
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">로딩 중...</span>
                    </Spinner>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-4">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    const systemHealthScore = calculateSystemHealth();
    const totalSecurityEvents = dashboardData?.summary?.security_events_today || 0;

    return (
        <SectionContainer title="🛡️ 보안 모니터링 대시보드" className="list">
            <Spinner visible={loading} />
            <Row className="mb-4">
                <Col>
                    <p className="text-muted">GlucoSOUND 시스템 무결성 실시간 모니터링</p>
                </Col>
            </Row>

            {/* 주요 메트릭 카드 */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="text-white bg-success">
                        <Card.Body className="text-center">
                            <i className="fas fa-check-circle fa-2x mb-2"></i>
                            <h3>{dashboardData?.integrity_monitoring?.integrity_checks?.passed_count || 0}</h3>
                            <p className="mb-1">무결성 검증 성공</p>
                            <small>{formatTime(dashboardData?.last_updated)}</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-white bg-warning">
                        <Card.Body className="text-center">
                            <i className="fas fa-exclamation-triangle fa-2x mb-2"></i>
                            <h3>{dashboardData?.summary?.failed_integrity_checks || 0}</h3>
                            <p className="mb-1">무결성 검증 실패</p>
                            <small>{formatTime(dashboardData?.last_updated)}</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-white bg-danger">
                        <Card.Body className="text-center">
                            <i className="fas fa-shield-alt fa-2x mb-2"></i>
                            <h3>{totalSecurityEvents}</h3>
                            <p className="mb-1">보안 이벤트 (24시간)</p>
                            <small>{formatTime(dashboardData?.last_updated)}</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-white bg-info">
                        <Card.Body className="text-center">
                            <i className="fas fa-heartbeat fa-2x mb-2"></i>
                            <h3>{systemHealthScore}%</h3>
                            <p className="mb-1">시스템 상태</p>
                            <small>{formatTime(dashboardData?.last_updated)}</small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                {/* 실시간 알림 */}
                <Col md={4}>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">
                                <i className="fas fa-bell text-warning me-2"></i>
                                실시간 알림
                            </h5>
                        </Card.Header>
                        <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {dashboardData?.recent_critical && dashboardData.recent_critical.length > 0 ? (
                                dashboardData.recent_critical.map((alert, index) => (
                                    <Alert key={index} variant={alert.risk_level === 'CRITICAL' ? 'danger' : 'warning'} className="py-2">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div>
                                                <strong>{alert.title}</strong>
                                                <div className="small">{alert.type}</div>
                                            </div>
                                            {getSeverityBadge(alert.risk_level)}
                                        </div>
                                        <div className="small text-muted mt-1">
                                            {formatTime(alert.created_at)}
                                        </div>
                                    </Alert>
                                ))
                            ) : (
                                <div className="text-muted text-center">알림이 없습니다.</div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* 보안 이벤트 차트 */}
                <Col md={8}>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">
                                <i className="fas fa-chart-pie text-info me-2"></i>
                                보안 이벤트 분석
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <div style={{ height: '300px' }}>
                                {getSecurityEventsChartData() && (
                                    <Doughnut 
                                        data={getSecurityEventsChartData()} 
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: {
                                                    position: 'bottom'
                                                }
                                            }
                                        }}
                                    />
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mt-4">
                {/* 무결성 검증 상태 */}
                <Col md={6}>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">
                                <i className="fas fa-check-double text-success me-2"></i>
                                무결성 검증 상태
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <div style={{ height: '250px' }}>
                                {getIntegrityChartData() && (
                                    <Bar 
                                        data={getIntegrityChartData()} 
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            scales: {
                                                y: {
                                                    beginAtZero: true,
                                                    ticks: {
                                                        stepSize: 1
                                                    }
                                                }
                                            },
                                            plugins: {
                                                legend: {
                                                    display: false
                                                }
                                            }
                                        }}
                                    />
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {/* 시스템 성능 */}
                <Col md={6}>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">
                                <i className="fas fa-tachometer-alt text-primary me-2"></i>
                                시스템 성능
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            {dashboardData?.integrity_monitoring?.system_metrics && (
                                <div className="row">
                                    <div className="col-6 mb-3">
                                        <div className="d-flex justify-content-between">
                                            <span>API 응답시간:</span>
                                            <strong>{dashboardData.integrity_monitoring.system_metrics.api_response_time || 0}ms</strong>
                                        </div>
                                        <div className="progress mt-1">
                                            <div 
                                                className="progress-bar bg-info" 
                                                style={{ width: `${Math.min((dashboardData.integrity_monitoring.system_metrics.api_response_time || 0) / 1000 * 100, 100)}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                    <div className="col-6 mb-3">
                                        <div className="d-flex justify-content-between">
                                            <span>메모리 사용량:</span>
                                            <strong>{dashboardData.integrity_monitoring.system_metrics.memory_usage || 0}MB</strong>
                                        </div>
                                        <div className="progress mt-1">
                                            <div 
                                                className="progress-bar bg-warning" 
                                                style={{ width: `${Math.min((dashboardData.integrity_monitoring.system_metrics.memory_usage || 0) / 512 * 100, 100)}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="d-flex justify-content-between">
                                            <span>DB 연결:</span>
                                            <strong>{dashboardData.integrity_monitoring.system_metrics.database_connections || 0}</strong>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="d-flex justify-content-between">
                                            <span>CPU 사용률:</span>
                                            <strong>{dashboardData.integrity_monitoring.system_metrics.cpu_usage || 0}%</strong>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {!dashboardData?.integrity_monitoring?.system_metrics && (
                                <div className="text-muted text-center">시스템 메트릭 데이터를 불러오는 중...</div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* 최근 보안 이벤트 */}
            <Row className="mt-4">
                <Col>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">
                                <i className="fas fa-list text-secondary me-2"></i>
                                최근 보안 이벤트
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {recentEvents.length > 0 ? (
                                    recentEvents.map((event, index) => (
                                        <div key={index} className={`p-2 mb-2 rounded ${event.severity === 'HIGH' || event.severity === 'CRITICAL' ? 'bg-danger bg-opacity-10 border-start border-danger border-4' : 'bg-light border-start border-primary border-4'}`}>
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <strong>{event.event_type}</strong>
                                                    {event.device_id && (
                                                        <Badge bg="secondary" className="ms-2">{event.device_id}</Badge>
                                                    )}
                                                    {getSeverityBadge(event.severity)}
                                                </div>
                                                <small className="text-muted">{formatTime(event.created_at)}</small>
                                            </div>
                                            {event.details && (
                                                <div className="small mt-1 text-muted">
                                                    {typeof event.details === 'string' ? event.details : JSON.stringify(event.details)}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-muted text-center">이벤트가 없습니다.</div>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </SectionContainer>
    );
};

export default SecurityMonitoring;
